import { clsx } from "clsx";
import { ScrollGridContainer } from "./ScrollGridContainer";
import { pxToRem } from "@ui/lib/utils";
import { Tab } from "../core/react-aria/Tabs";

export interface ScrollGridProps<T> {
  items: T[];
  renderItem: (item: T, index: number, { isSelected }: { isSelected: boolean }) => React.ReactNode;
  className?: string;
  columns?: 3 | 4 | 5;
  alignNavButtonsToRatio?: number;
  /** Vertical alignment of items within the scroller row (flexbox `align-items`). Defaults to `stretch`. */
  itemsAlign?: "start" | "center" | "end" | "stretch";
  legend?: { show: boolean; getItemTitle: (item: T) => string; textClassName?: string; className?: string };
  gap?: "default" | "lg";
  as?: "list" | "tabs";
  draggable?: boolean;
  /** Ref to the horizontal scroll viewport (the list / carousel scroller). */
  ref?: React.Ref<HTMLElement>;
  onChangeLeftmostIndex?: (index: number) => void;
}

export function ScrollGrid<T>({
  items,
  renderItem,
  className,
  columns = 3,
  gap = "default",
  alignNavButtonsToRatio,
  itemsAlign,
  legend,
  as = "list",
  ref,
  draggable = true,
  onChangeLeftmostIndex,
}: ScrollGridProps<T>) {
  const itemCount = items.length;
  const allVisibleOnDesktop = itemCount <= columns;

  // Internal options. Hard-coded for now to ensure consistency but
  // could be moved to props if needed.
  const snap = true;
  const navButtons = true;
  const scrollbar = "none";
  const peek = false;

  const itemClassName = clsx("tw-flex-none tw-w-[var(--w)]", snap && "tw-snap-start");

  return (
    <ScrollGridContainer
      ref={ref}
      onChangeLeftmostIndex={onChangeLeftmostIndex}
      scrollbar={scrollbar}
      snap={snap}
      draggable={draggable}
      itemCount={itemCount}
      columns={columns}
      navButtons={navButtons}
      peek={peek}
      as={as}
      alignNavButtonsToRatio={alignNavButtonsToRatio}
      itemsAlign={itemsAlign}
      {...(legend?.show && itemCount > 3
        ? {
            legend: {
              className: legend.className,
              textClassName: legend.textClassName,
              titles: items.map(legend.getItemTitle),
            },
          }
        : {})}
      style={
        {
          containerType: alignNavButtonsToRatio ? "inline-size" : undefined,
          "--gap": pxToRem(gap === "lg" ? 40 : 16),
        } as React.CSSProperties
      }
      className={clsx(
        className,
        [
          "tw-var-[container=100%]",
          alignNavButtonsToRatio &&
            "supports-[container-type:inline-size]:tw-var-[container=calc(100cqw-var(--full-bleed)*2)]",
        ],
        "tw-var-[w=min(var(--max,999rem),calc((var(--container)-(var(--gap)*var(--gaps))-var(--peek))/var(--cols)))]",
        "tw-var-[peek=calc(2rem-var(--full-bleed))] lg:tw-var-[peek=calc(3rem-var(--full-bleed))]",
        columns === 3 && [
          "tw-var-[max=20rem] lg:tw-var-[max=unset]",
          "tw-var-[cols=1] sm:tw-var-[cols=2] lg:tw-var-[cols=3]",
          "tw-var-[gaps=1] sm:tw-var-[gaps=2]",
          (!peek || allVisibleOnDesktop) && "lg:!tw-var-[peek=0px]",
        ],
        columns === 4 && [
          "tw-var-[max=20rem]",
          "tw-var-[cols=1] sm:tw-var-[cols=2] lg:tw-var-[cols=3] xl:tw-var-[cols=4]",
          "tw-var-[gaps=1] sm:tw-var-[gaps=2] lg:tw-var-[gaps=3]",
          (!peek || allVisibleOnDesktop) && "xl:!tw-var-[peek=0px]",
        ],
        columns === 5 && [
          "tw-var-[max=15rem]",
          "tw-var-[cols=1] min-[32rem]:tw-var-[cols=2] md:tw-var-[cols=3] lg:tw-var-[cols=4] xl:tw-var-[cols=5]",
          "tw-var-[gaps=1] min-[32rem]:tw-var-[gaps=2] md:tw-var-[gaps=3] lg:tw-var-[gaps=4]",
          (!peek || allVisibleOnDesktop) && "xl:!tw-var-[peek=0px]",
        ],
      )}
    >
      {as === "tabs"
        ? items.map((item, index) => (
            <Tab
              key={index}
              id={index}
              className={clsx(itemClassName, "tw-rounded tw-outline-none focus-visible:tw-outline-focus")}
            >
              {({ isSelected }) => renderItem(item, index, { isSelected })}
            </Tab>
          ))
        : items.map((item, index) => (
            <li key={index} className={itemClassName}>
              {renderItem(item, index, { isSelected: false })}
            </li>
          ))}
    </ScrollGridContainer>
  );
}
